import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import dayjs from "dayjs";
import { efolderUploadEnabled, fetchEfolderStatus } from "../../service/efolderUpload";
import { status as statusTokens } from "../../theme/tokens";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Line = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProcessingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${statusTokens.processing.bg};
  color: ${statusTokens.processing.fg};
  border: 1px solid ${statusTokens.processing.border};

  &::before {
    content: "";
    width: 9px;
    height: 9px;
    border-radius: 50%;
    border: 2px solid ${statusTokens.processing.border};
    border-top-color: ${statusTokens.processing.fg};
    animation: ${spin} 0.9s linear infinite;
  }
`;

export const PULL_TOOLTIP = "A pull with no new documents still updates this";
const RECENT_PULL_MS = 24 * 60 * 60 * 1000;

const FIELDS = [
  "last_efolder_pull_at",
  "last_efolder_newest_receipt_date",
  "efolder_pull_count",
  "documents_processing_count",
];

const hasPullFields = (matter) => matter && FIELDS.slice(0, 3).some((key) => matter[key] !== undefined);

// The matter detail payload already carries the pull fields (section 7);
// when it does not (an older backend, or a caller that only has the id),
// the bulk status endpoint fills them in for this one matter.
export const useEfolderPullStatus = (matterId, matter) => {
  const enabled = efolderUploadEnabled();
  const fromMatter = hasPullFields(matter);
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (!enabled || fromMatter || !matterId) return undefined;
    let live = true;
    fetchEfolderStatus([matterId])
      .then((rows) => {
        if (live) setFetched(rows?.[0] || null);
      })
      .catch(() => {
        if (live) setFetched(null);
      });
    return () => {
      live = false;
    };
  }, [enabled, fromMatter, matterId]);
  if (!enabled) return null;
  const source = fromMatter ? matter : fetched;
  if (!source) return null;
  return {
    lastPullAt: source.last_efolder_pull_at || null,
    newestReceiptDate: source.last_efolder_newest_receipt_date || null,
    pullCount: source.efolder_pull_count ?? null,
    processingCount: source.documents_processing_count ?? matter?.documents_processing_count ?? 0,
  };
};

export const formatPullLine = ({ lastPullAt, newestReceiptDate, pullCount }) => {
  const parts = [`Last eFolder pull: ${dayjs(lastPullAt).format("MMM D, YYYY")}`];
  if (newestReceiptDate) parts.push(`newest record ${dayjs(newestReceiptDate).format("MMM D, YYYY")}`);
  if (pullCount) parts.push(`${pullCount} pull${pullCount === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

/**
 * "Last eFolder pull: Oct 29, 2026 · newest record Sep 30, 2026 · 3 pulls"
 * (spec 9.3). Hidden until the matter has had a pull. While the pipeline
 * is still working through a recent pull it adds a spinner chip.
 */
const EfolderPullBadge = ({ matterId, matter, now }) => {
  const pull = useEfolderPullStatus(matterId, matter);
  if (!pull || !pull.lastPullAt) return null;
  const current = now ?? Date.now();
  const recent = current - new Date(pull.lastPullAt).getTime() < RECENT_PULL_MS;
  const processing = Number(pull.processingCount || 0);
  return (
    <Line data-testid="efolder-pull-badge" title={PULL_TOOLTIP}>
      <span>{formatPullLine(pull)}</span>
      {processing > 0 && recent && (
        <ProcessingChip aria-label={`AI processing ${processing}`}>AI processing {processing}</ProcessingChip>
      )}
    </Line>
  );
};

EfolderPullBadge.propTypes = {
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matter: PropTypes.shape({
    last_efolder_pull_at: PropTypes.string,
    last_efolder_newest_receipt_date: PropTypes.string,
    efolder_pull_count: PropTypes.number,
    documents_processing_count: PropTypes.number,
  }),
  // Injected by tests so "recent" is deterministic.
  now: PropTypes.number,
};

export default EfolderPullBadge;
